import React, { useEffect, useState } from 'react';
import { useAppState } from '../../context/AppStateContext';
import {
  SupportAccessService, SupportActivity, SupportActivityItem, apiErrorMessage,
  formatSupportExpiry, childCode,
} from '../../services/supportDataService';

interface Props {
  grantId: number | null;
  onClose: () => void;
}

/** 'retention_years_after_leaving' → 'Retention years after leaving'. */
function fieldLabel(field: string): string {
  const words = field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function valueText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const EVENT_LABEL: Record<string, string> = {
  requested: 'Support requested access',
  approved: 'Access approved',
  offered: 'Access given',
  declined: 'Request declined',
  revoked: 'Access revoked',
  expired: 'Access expired',
  diagnostics_on: 'Post and report status turned on',
  diagnostics_off: 'Post and report status turned off',
};

/**
 * What Rustle It Support did under one grant (tdp-api docs/support-access-plan.md §7):
 * the grant's lifecycle, then each visit — views summarised by area, changes
 * one per line, blocked attempts shown so the limits are visible. Child codes
 * support saw (C-0042) are shown with the real name, which the admin can see
 * anyway.
 */
const SupportActivityModal: React.FC<Props> = ({ grantId, onClose }) => {
  const { childProfiles } = useAppState();
  const [data, setData] = useState<SupportActivity | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (grantId === null) return;
    setData(null);
    setError(null);
    SupportAccessService.fetchActivity(grantId)
      .then(setData)
      .catch(err => setError(apiErrorMessage(err, 'Could not load support activity.')));
  }, [grantId]);

  if (grantId === null) return null;

  const entityText = (item: SupportActivityItem): string | null => {
    if (item.entityType === 'child' && item.entityId) {
      const child = childProfiles.find(c => c.id === item.entityId);
      const name = child ? [child.first_name, child.last_name].filter(Boolean).join(' ') : null;
      return name ? `${childCode(item.entityId)} (${name})` : childCode(item.entityId);
    }
    return null;
  };

  const download = async () => {
    try {
      const blob = await SupportAccessService.downloadActivityCsv(grantId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-activity-${grantId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not download the activity log.'));
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="support-activity-title" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="support-activity-title">Support activity</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body support-activity">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {!data && !error && <p className="text-muted mb-0">Loading…</p>}
            {data && (
              <>
                <p className="support-activity__reason"><strong>Reason:</strong> {data.grant.reason}</p>

                <ul className="support-activity__events">
                  {data.events.map((e, i) => (
                    <li key={i}>
                      <span className="support-activity__when">{formatSupportExpiry(e.at)}</span>
                      {EVENT_LABEL[e.kind] ?? e.kind}
                      {e.diagnostics ? ', including post and report status' : ''}
                      {e.by ? ` · ${e.by}` : ''}
                    </li>
                  ))}
                </ul>

                {data.visits.length === 0 ? (
                  <p className="text-muted">Support has not opened your organisation under this access.</p>
                ) : data.visits.map((v, i) => (
                  <section key={i} className="support-activity__visit">
                    <div className="support-activity__visit-head">
                      Visit {i + 1} · {formatSupportExpiry(v.startedAt)}–{formatSupportExpiry(v.endedAt).split(', ')[1]} · {v.userName}
                    </div>
                    <ul>
                      {v.items.map((item, j) => {
                        const entity = entityText(item);
                        return (
                          <li key={j} className={`support-activity__item support-activity__item--${item.kind}`}>
                            <i className={`fas ${item.kind === 'blocked' ? 'fa-ban' : item.kind === 'change' ? 'fa-pen' : 'fa-eye'}`} aria-hidden="true" />
                            <span>
                              {item.kind === 'blocked' ? `Tried to: ${item.label} (blocked)` : item.label}
                              {entity ? ` · ${entity}` : ''}
                              {item.count > 1 ? <span className="support-activity__count"> ×{item.count}</span> : null}
                              {item.kind === 'change' && item.details && item.details.length > 0 && (
                                <ul className="support-activity__diff">
                                  {item.details.flatMap((d, k) =>
                                    d.operation === 'INSERT' ? [<li key={`${k}-new`}>Created</li>]
                                    : d.operation === 'DELETE' ? [<li key={`${k}-del`}>Deleted</li>]
                                    : d.changes.map(c => (
                                      <li key={`${k}-${c.field}`}>
                                        {fieldLabel(c.field)}: <span className="support-activity__before">{valueText(c.before)}</span>
                                        {' → '}<span className="support-activity__after">{valueText(c.after)}</span>
                                      </li>
                                    )))}
                                </ul>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </>
            )}
          </div>
          <div className="modal-footer">
            {data && data.requestCount > 0 && (
              <button type="button" className="btn btn-outline-secondary me-auto" onClick={download}>
                <i className="fas fa-download me-2" aria-hidden="true" />Download CSV
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportActivityModal;
